import React, { createContext, useState, useContext, ReactNode } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Box, useTheme } from '@mui/material';
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';

// Define severity types for the alert
export type AlertSeverity = 'info' | 'warning' | 'error';

type AlertContextType = {
    openAlert: (title: string, message: string, buttonText: string, onConfirm?: () => void, showCancel?: boolean, error?: Error, errorContext?: Record<string, any>, severity?: AlertSeverity) => void;
    closeAlert: () => void;
};

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const appInsights = useAppInsightsContext();
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [confirmCallback, setConfirmCallback] = useState<(() => void) | undefined>(undefined);
    const [showCancelButton, setShowCancelButton] = useState(true);
    const [buttonText, setButtonText] = useState('Ok');
    const [severity, setSeverity] = useState<AlertSeverity>('warning');
    const { t } = useTranslation();
    const theme = useTheme();

    const openAlert = (
        title: string,
        message: string,
        buttonText: string = "Ok",
        onConfirm?: () => void,
        showCancel: boolean = true,
        error?: Error,
        errorContext?: Record<string, any>,
        severity: AlertSeverity = 'warning' // Default severity is warning (yellow)
    ) => {
        // Track exception if error is provided
        if (error) {
            appInsights.trackException({
                exception: error,
                severityLevel: 3, // Error
                properties: {
                    alertTitle: title,
                    alertMessage: message,
                    alertSeverity: severity,
                    ...errorContext
                }
            });
        }

        setTitle(title);
        setMessage(message);
        setConfirmCallback(() => onConfirm);
        setShowCancelButton(showCancel);
        setOpen(true);
        setButtonText(buttonText);
        setSeverity(severity);
    };

    const closeAlert = () => {
        setOpen(false);
    };

    const handleConfirm = () => {
        if (confirmCallback) confirmCallback();
        closeAlert();
    };

    // Get appropriate color based on severity
    const getSeverityConfig = () => {
        switch (severity) {
            case 'error':
                return theme.palette.mode === 'dark'
                    ? {
                        bgcolor: '#35181D',
                        color: '#FFE3E5',
                        actionBgcolor: '#FF8A8A',
                        actionColor: '#35181D',
                        icon: <ErrorIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#FF8A8A' }} />
                    }
                    : {
                        bgcolor: '#FDEDED',
                        color: '#5F2120',
                        actionBgcolor: '#5F2120',
                        actionColor: '#FFFFFF',
                        icon: <ErrorIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#d32f2f' }} />
                    };
            case 'warning':
                return theme.palette.mode === 'dark'
                    ? {
                        bgcolor: theme.palette.warning.light,
                        color: theme.palette.warning.contrastText,
                        actionBgcolor: theme.palette.warning.main,
                        actionColor: '#202634',
                        icon: <WarningIcon sx={{ mr: 1, verticalAlign: 'middle', color: theme.palette.warning.main }} />
                    }
                    : {
                        bgcolor: '#FFF4E5',
                        color: '#663C00',
                        actionBgcolor: '#663C00',
                        actionColor: '#FFFFFF',
                        icon: <WarningIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#ed6c02' }} />
                    };
            case 'info':
            default:
                return theme.palette.mode === 'dark'
                    ? {
                        bgcolor: theme.palette.spectre.surfaceMuted,
                        color: theme.palette.spectre.textPrimary,
                        actionBgcolor: '#2563EB',
                        actionColor: '#FFFFFF',
                        icon: <InfoIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#67E8F9' }} />
                    }
                    : {
                        bgcolor: '#FFFFFFFF',
                        color: '#014361',
                        actionBgcolor: '#014361',
                        actionColor: '#FFFFFF',
                        icon: <InfoIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#0288d1' }} />
                    };
        }
    };

    const severityConfig = getSeverityConfig();

    // Parse message to separate main message from error details
    const renderMessageContent = () => {
        const parts = message.split('\n\n');
        if (parts.length > 1) {
            // Has error details after double newline
            const mainMessage = parts[0];
            const errorDetails = parts.slice(1).join('\n\n');
            
            return (
                <>
                    <Box>{mainMessage}</Box>
                    <Box sx={{
                        mt: 2,
                        p: 1.5,
                        bgcolor: 'rgba(0, 0, 0, 0.05)',
                        borderRadius: 1,
                        fontFamily: 'Courier New, monospace',
                        fontSize: '0.875rem',
                        color: severityConfig.color,
                        wordBreak: 'break-word',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        whiteSpace: 'pre-wrap'
                    }}>
                        {errorDetails}
                    </Box>
                </>
            );
        }
        return <Box>{message}</Box>;
    };

    return (
        <AlertContext.Provider value={{ openAlert, closeAlert }}>
            {children}
            <Dialog open={open} onClose={showCancelButton ? closeAlert : undefined}>
                <DialogTitle sx={{ bgcolor: severityConfig.bgcolor, color: severityConfig.color, display: 'flex', alignItems: 'center' }}>
                    {severityConfig.icon}
                    {title}
                </DialogTitle>
                <DialogContent sx={{ bgcolor: severityConfig.bgcolor }}>
                    <DialogContentText component="div" sx={{ color: severityConfig.color, whiteSpace: 'pre-line' }}>
                        {renderMessageContent()}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ bgcolor: severityConfig.bgcolor }}>
                    {showCancelButton && (
                        <Button
                            onClick={closeAlert}
                            variant="outlined"
                            sx={{
                                color: severityConfig.color,
                                borderColor: severityConfig.color,
                                textTransform: 'none',
                                fontWeight: 500,
                                '&:hover': {
                                    borderColor: severityConfig.color,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.08)'
                                        : 'rgba(0, 0, 0, 0.05)'
                                }
                            }}
                        >
                            {t('alertContext.cancel')}
                        </Button>
                    )}
                    <Button 
                        onClick={handleConfirm} 
                        autoFocus
                        variant="contained"
                        sx={{ 
                            bgcolor: severityConfig.actionBgcolor,
                            color: severityConfig.actionColor,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 3,
                            py: 1,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            '&:hover': {
                                bgcolor: severityConfig.actionBgcolor,
                                opacity: 0.9,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                            }
                        }}
                    >
                        {buttonText}
                    </Button>
                </DialogActions>
            </Dialog>
        </AlertContext.Provider>
    );
};

export const useAlert = () => {
    const context = useContext(AlertContext);
    if (context === undefined) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};